import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const LocalSEOPage: React.FC = () => {
  const benefits = [
    {
      icon: 'ri-map-pin-2-line',
      title: 'Local Search Dominance',
      description: 'Appear at the top when customers search for your services in your area.'
    },
    {
      icon: 'ri-user-location-line',
      title: 'Targeted Traffic',
      description: 'Attract customers who are actively looking for your services nearby.'
    },
    {
      icon: 'ri-store-line',
      title: 'More Foot Traffic',
      description: 'Drive more customers to your physical location with local search optimization.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Beat Competitors',
      description: 'Outrank your local competitors and capture more market share.'
    }
  ];

  const features = [
    'Google My Business optimization',
    'Local keyword research',
    'Citation building & management',
    'Local content creation',
    'Review management strategy',
    'Local link building',
    'Schema markup implementation',
    'Local search analytics'
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center bg-gradient-to-r from-orange-100 to-red-100 rounded-full px-6 py-2 mb-6">
            <i className="ri-map-pin-line text-orange-600 mr-2"></i>
            <span className="text-orange-800 font-medium">Local SEO Services</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent">
              Dominate Local Search
            </span>
            <br />
            In Your Area
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Get found by local customers when they need your services most. Our local SEO strategies help small businesses outrank competitors in their area.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-white text-red-600 border-2 border-red-600 px-8 py-4 rounded-full font-semibold hover:bg-red-50 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Why Local SEO is Critical for Small Businesses
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              97% of consumers search online for local businesses. Make sure they find you first, not your competitors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  Comprehensive Local SEO Services
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We optimize every aspect of your local search presence to ensure maximum visibility when customers are looking for your services.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/6cdb3a2e9613b1e52fe6d41d20d346a3.jpg"
                alt="Local SEO Results"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-600 via-red-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Dominate Local Search Results?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let us help you outrank your competitors and attract more local customers to your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/consultation"
              className="bg-white text-red-600 px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-red-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6">
            <a href="#" className="text-white hover:text-orange-200 transition-colors duration-300">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-orange-200 transition-colors duration-300">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-orange-200 transition-colors duration-300">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-orange-200 transition-colors duration-300">
              <i className="ri-linkedin-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default LocalSEOPage;
